import { createRootRoute, Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { SessionProvider, useSession } from "../lib/session";
import { db } from "../lib/offline/db";
import { syncQueue } from "../lib/offline/sync";
import { LicenseGate } from "../components/LicenseGate";

export const Route = createRootRoute({ component: () => (
  <SessionProvider><Shell /></SessionProvider>
) });

const NAV = [
  { to: "/", label: "Inicio", ico: "📈" },
  { to: "/pos", label: "POS (Vender)", ico: "⚡" },
  { to: "/ventas", label: "Ventas", ico: "🧮" },
  { to: "/catalogo", label: "Catálogo", ico: "🏷️" },
  { to: "/inventario", label: "Inventario", ico: "📦" },
  { to: "/compras", label: "Compras", ico: "🚚" },
  { to: "/clientes", label: "Clientes", ico: "👥" },
  { to: "/fiscal", label: "Fiscal", ico: "🧾" },
  { to: "/reportes", label: "Reportes", ico: "🎯" },
  { to: "/admin", label: "Configuración", ico: "⚙️" },
];

function Shell() {
  const { session, loading, branches, activeBranchId, setActiveBranch, role, signOut } = useSession();
  const nav = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isPublic = pathname === "/login" || pathname === "/registro";

  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // Ventas en cola sin sincronizar (pending/syncing/error).
  const pending = useLiveQuery(() => db.queuedSales.where("status").anyOf("pending", "syncing", "error").count(), [], 0);

  // Guard: sin sesión → login (excepto en rutas públicas).
  useEffect(() => {
    if (!loading && !session && !isPublic) nav({ to: "/login" });
  }, [loading, session, isPublic, nav]);

  if (isPublic) return <Outlet />;
  if (loading) return <div className="flex min-h-screen items-center justify-center text-grafito-400">Cargando…</div>;
  if (!session) return null; // redirigiendo a login

  return (
    <div className="flex min-h-screen">
      <aside className="w-60 shrink-0 bg-grafito-900 text-grafito-50 p-4">
        <div className="mb-6 text-lg font-extrabold">🔧 FerreSaaS</div>
        <nav className="space-y-1">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-white/10"
              activeProps={{ className: "bg-brand text-white" }}>
              <span>{n.ico}</span> {n.label}
            </Link>
          ))}
        </nav>
        <button onClick={() => { void signOut(); nav({ to: "/login" }); }}
          className="mt-6 w-full rounded-lg px-3 py-2 text-left text-sm text-grafito-400 hover:bg-white/10">
          ↩ Salir
        </button>
      </aside>

      <div className="flex-1 flex flex-col">
        <header className="topbar flex items-center gap-4 border-b bg-white px-6 py-3">
          {/* Selector de sucursal activa (reemplaza VITE_DEMO_BRANCH_ID). */}
          <select value={activeBranchId ?? ""} onChange={(e) => setActiveBranch(e.target.value)}
            className="rounded-lg border px-2 py-1 text-sm">
            {branches.length === 0 && <option value="">— sin sucursal —</option>}
            {branches.map((b) => <option key={b.id} value={b.id}>🏬 {b.name}</option>)}
          </select>
          <span className="text-xs uppercase tracking-wide text-grafito-400">{role}</span>
          <div className="ml-auto flex items-center gap-3 text-sm">
            {pending > 0 && (
              <button onClick={() => void syncQueue()} title="Ventas por sincronizar"
                className="flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-200">
                ⏳ {pending} por sincronizar
              </button>
            )}
            <span className={online ? "text-green-600" : "text-offline font-semibold"}>
              {online ? "● En línea" : "● Sin conexión (contingencia)"}
            </span>
          </div>
        </header>
        <main className="flex-1 p-6">
          {activeBranchId
            ? <LicenseGate><Outlet /></LicenseGate>
            : <div className="rounded-xl border bg-white p-8 text-center text-grafito-400">No tienes sucursales asignadas.</div>}
        </main>
      </div>
    </div>
  );
}
